import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { populationsApi, type AudienceReadiness } from "@/lib/api";
import { ruleCopy } from "@/features/studies/questionnaire/utils/questionnaireIssueCopy";

/**
 * Why a study cannot launch on this audience, as the launch refusal says it:
 * the same catalog line (and params) the launch toast renders. Nothing when
 * the audience is ready or its readiness is unknown.
 */
export function AudienceReadinessNotice({
  readiness,
  className,
}: {
  readiness: AudienceReadiness | null | undefined;
  className?: string;
}) {
  if (!readiness || readiness.ready) return null;
  const line = ruleCopy(readiness.code, readiness.params);
  if (!line) return null;
  return (
    <div
      role="status"
      data-testid="audience-readiness-notice"
      className={`flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200 ${className ?? ""}`}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p>{line}</p>
    </div>
  );
}

/** The audience's launch readiness, read from its detail (the one projection). */
export function useAudienceReadiness(
  audienceId: string | null | undefined,
): AudienceReadiness | null {
  const [readiness, setReadiness] = useState<AudienceReadiness | null>(null);
  useEffect(() => {
    setReadiness(null);
    if (!audienceId) return;
    let cancelled = false;
    populationsApi
      .getAudienceUiDetail(audienceId)
      .then((detail) => {
        if (!cancelled) setReadiness(detail.readiness ?? null);
      })
      .catch(() => {
        // Unknown readiness shows nothing; launch still refuses with its own line.
      });
    return () => {
      cancelled = true;
    };
  }, [audienceId]);
  return readiness;
}
